import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import VideoFeedPageSkeleton from "@/components/skeletons/VideoFeedPageSkeleton";
import { FollowingVideoFeedSkeleton } from "@/components/skeletons/FollowingFeedPageSkeleton";
import SchedulesListPageSkeleton from "@/components/skeletons/SchedulesListPageSkeleton";
import ComparisonListPageSkeleton from "@/components/skeletons/ComparisonListPageSkeleton";
import ComparisonDetailPageSkeleton from "@/components/skeletons/ComparisonDetailPageSkeleton";
import { MeProfileSettingsSkeleton } from "@/components/skeletons/routeSkeleton";
import {
  SettingsAppliedJobsPageSkeleton,
  SettingsSavedPageSkeleton,
} from "@/components/skeletons/SettingsSavedAppliedSkeletons";

/** Centered shell matching main content area under header */
function Shell({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        "w-full min-h-[calc(100vh-65px)] lg:min-h-[calc(100vh-104px)] px-4 py-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Home — نفس `skeletonForRoute("/")` و`VideoFeedPageSkeleton` حتى لا يظهر شكلان عند:
 * تنقّل الشريط الجانبي (overlay) ثم تحميل الصفحة / انتظار الفيديو.
 */
export function HomeFeedSkeleton() {
  return <VideoFeedPageSkeleton />;
}

/** Following: فيديو 9/16 + عمود فارغ 380px (ليس كالرئيسية مع قائمة المقترحين) */
export function FollowingFeedSkeleton() {
  return <FollowingVideoFeedSkeleton />;
}

/** Settings hub: قائمة + عمود نماذج (مثل `SettingsLayoutPage`) */
export function SettingsPageSkeleton() {
  return <MeProfileSettingsSkeleton />;
}

/** محفوظات الفيديو — شبكة بطاقات مثل `SavedContent` */
export { SettingsSavedPageSkeleton };

/** الوظائف المقدَّمة — مجموعات شركة + بطاقات مثل `AppliedJobsContent` */
export { SettingsAppliedJobsPageSkeleton };

/** Schedules — نفس `SchedulesListPageSkeleton` ومسار التنقّل */
export function SchedulesListSkeleton() {
  return <SchedulesListPageSkeleton />;
}

/** Comparison index — نفس `ComparisonListPageSkeleton` */
export function ComparisonIndexSkeleton() {
  return <ComparisonListPageSkeleton />;
}

/** Comparison detail — نفس `ComparisonDetailPageSkeleton` */
export function ComparisonDetailSkeleton() {
  return <ComparisonDetailPageSkeleton />;
}

/** Profile / me page */
export function MeProfileSkeleton() {
  return (
    <Shell className="max-w-4xl mx-auto">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <Skeleton className="size-28 rounded-full shrink-0" />
          <div className="flex-1 space-y-3 w-full">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-full max-w-md" />
            <Skeleton className="h-4 w-3/4 max-w-sm" />
            <div className="flex gap-2 pt-2">
              <Skeleton className="h-10 w-28 rounded-full" />
              <Skeleton className="h-10 w-28 rounded-full" />
            </div>
          </div>
        </div>
        <Skeleton className="h-32 w-full rounded-xl" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      </div>
    </Shell>
  );
}

/** Lazy route fallback (generic) */
export function RouteFallbackSkeleton() {
  return (
    <Shell className="flex flex-col gap-4 max-w-3xl mx-auto">
      <Skeleton className="h-10 w-2/3 max-w-sm rounded-md" />
      <Skeleton className="h-4 w-full rounded-md" />
      <Skeleton className="h-4 w-5/6 rounded-md" />
      <div className="grid gap-3 pt-4">
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    </Shell>
  );
}
